import { optimizeCss } from '@wix/css-optimization'
import type {
	BeckyCssModuleResponse,
	BeckyFunctionLibrary,
	BeckyModel,
	BeckyRequestPayload,
	SiteAssetsModule,
	CssInstanceModel,
} from '@wix/thunderbolt-becky-root'
import { createFunctionLibrary, deStringifyExperiments } from '@wix/thunderbolt-becky-root'
import { getCatharsisCssString } from '@wix/thunderbolt-catharsis-deployable'
import { fetchComponentShapes } from '@wix/thunderbolt-fetch-shapes'
import { resolveBeckyModel } from './helpers/resolveBeckyModel'
import { getStylableInstance } from './stylable'
import byRefModule from '../byRef'
import { BeckyExperiments } from '@wix/thunderbolt-commons'
import { BeckyTopology, FetchFn, MetricsReporter, ModuleFetcher, Logger } from '@wix/thunderbolt-becky-types'
import { maybeAppendErrorPages } from '../errorPages/getPayloadWithErrorPage'
import { BeckyFetch } from './utils/beckyFetch'
import { getBeckyTopology } from './helpers/topologyBuilder'
import { processOoiCssImpl } from './helpers/ooiCssProcessor'
import LRU from 'lru-cache'
import { cssModelFactory, ooiCssModelFactory } from '../carmiEntries/css-entry'
import schemas from '@wix/document-services-json-schemas/dist/refData.json'

export const getCssResult = async ({
	payload,
	beckyTopology,
	funcLib,
	moduleFetcher,
	fetchFn,
	metricsReporter,
}: {
	payload: BeckyRequestPayload
	beckyTopology: BeckyTopology
	funcLib: BeckyFunctionLibrary
	moduleFetcher: ModuleFetcher
	fetchFn: FetchFn
	metricsReporter: MetricsReporter
}): Promise<CssInstanceModel> => {
	const beckyModel = await resolveBeckyModel({ payload, beckyTopology, fetchFn, metricsReporter })

	const shapeStore = await funcLib.runAsyncAndReport(
		async () =>
			fetchComponentShapes(
				beckyModel,
				fetchFn,
				beckyTopology.environmentTopology.mediaRootUrl,
				funcLib.buildSvgUrl,
				metricsReporter,
				schemas
			),
		'tb-css-fetch-shapes'
	)

	const beckyModelWithShapes: BeckyModel = { ...beckyModel, shapeStore }

	const ccsFuncLib = { ...funcLib, setFetchRequestsStore: () => metricsReporter.reportError }
	// Replace setter with a noop function because fetchApi.carmi assumes that the setter exists
	const ooiCssModel = funcLib.runAndReport(
		() => ooiCssModelFactory(beckyModelWithShapes, ccsFuncLib),
		'ooiCssModelFactory'
	)

	const processOoiCss = await funcLib.runAsyncAndReport(
		() =>
			processOoiCssImpl(ooiCssModel, {
				funcLib,
				moduleFetcher,
				payload,
				beckyModel,
				metricsReporter,
			}),
		'processOoiCssImpl'
	)

	return funcLib.runAndReport(() => {
		const catharsisCss = funcLib.runAndReport(
			() => getCatharsisCssString(beckyModelWithShapes, schemas, funcLib.runAndReport),
			'end get-catharsis-css'
		)
		const result = funcLib.runAndReport(
			() =>
				cssModelFactory(beckyModelWithShapes, {
					...ccsFuncLib,
					processOoiCss,
				}),
			'end get-carmi-css'
		)
		return { ...result, css: catharsisCss + result.css }
	}, 'tb-css-model-calculation')
}

const getCssResultWithStylableInstance = async (
	payload: BeckyRequestPayload,
	beckyTopology: BeckyTopology,
	experiments: BeckyExperiments,
	fetchFn: FetchFn,
	metricsReporter: MetricsReporter,
	funcLib: BeckyFunctionLibrary,
	moduleFetcher: ModuleFetcher,
	logger: Logger
) => {
	const themeData = (await payload.pages.masterPage).data.theme_data.THEME_DATA
	const themeFontsSpxResolved = funcLib.resolveThemeFontsSpx(themeData.font)
	const maybeStylableInstance = await getStylableInstance({
		payload,
		fetch: fetchFn,
		fileRepoUrl: beckyTopology.environmentTopology.fileRepoUrl,
		metricsReporter,
		cache: stylableCache,
		themeData,
		themeFontsSpxResolved,
		staticMediaUrl: beckyTopology.publicTopology.staticMediaUrl,
	})

	const { css, fontUrlsToPreload } = await getCssResult({
		payload: {
			stylableInstance: maybeStylableInstance,
			...payload,
		},
		beckyTopology,
		funcLib,
		moduleFetcher,
		fetchFn,
		metricsReporter,
	})

	try {
		logger.info('Before CSS optimization', { cssSize: css.length })

		const optimizedCss = await metricsReporter.runAsyncAndReport(async () => {
			return await optimizeCss(css, {
				variablize: experiments['specs.thunderbolt.optimizeCSS_variablize'] === true,
			})
		}, 'tb-css-optimization')

		logger.info('After CSS optimization', {
			cssSize: optimizedCss.length,
		})

		return {
			css: optimizedCss,
			fontUrlsToPreload,
		}
	} catch (e) {
		metricsReporter.reportError(new Error(`Optimizing CSS failed: ${e}`))
	}

	return { css, fontUrlsToPreload }
}

const stylableCache = new LRU<string, any>({ max: 20 })

const cssModule: SiteAssetsModule<BeckyCssModuleResponse> = (moduleContext) => {
	const { httpClient, metricsReporter, topologyGetter, moduleFetcher, logger } = moduleContext
	const fetchFn = BeckyFetch(httpClient.fetch, metricsReporter, logger)
	const funcLib = createFunctionLibrary({
		fetch: fetchFn,
		metricsReporter,
		schemas,
	})
	const byRef = byRefModule(moduleContext)

	return {
		execute: async (payload) => {
			const experiments = deStringifyExperiments(payload.beckyExperiments)
			const beckyTopology = getBeckyTopology(topologyGetter, payload)

			return byRef
				.execute(maybeAppendErrorPages(payload))
				.then((payloadWithResolvedRefs) =>
					getCssResultWithStylableInstance(
						payloadWithResolvedRefs,
						beckyTopology,
						experiments,
						fetchFn,
						metricsReporter,
						funcLib,
						moduleFetcher,
						logger
					)
				)
		},
	}
}

export default cssModule
